/**
 * Vendor questionnaires — the sending side.
 *
 * The register answers one question at a glance: who have we asked, and did they
 * answer? So each row carries the vendor, the questionnaire, its status, the due
 * date, how far through the supplier is, and the score if they finished. A status
 * alone would not be enough: "IN_PROGRESS, 4 of 147" and "IN_PROGRESS, 140 of
 * 147" mean completely different things to whoever has to chase it.
 *
 * Mobile-first throughout, matching Vendors.tsx: cards rather than a table, the
 * send dialog as a bottom sheet, and 44px touch targets from the shared
 * primitives.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, Button, Pill, Modal, Field, EmptyState, Skeleton,
  ErrorNotice, StatTile, ProgressBar, TONE_FOR_STATUS, type Tone,
} from '../components/ui';
import { IconAssessment, IconShield } from '../components/icons';
import {
  LANGUAGES, LANGUAGE_NAMES, VENDOR_ASSESSMENT_STATUSES,
  VENDOR_COMPLIANCE_BANDS, type VendorComplianceBand, type Language,
} from '@opencomply/shared';
import { cn } from '../lib/cn';

/**
 * Posture row — one assessment as the aggregate dashboard sees it. Distinct from
 * the richer `Assessment` the detail modal loads: this is what a table row needs.
 */
interface PostureRow {
  id: string;
  vendorId: string;
  status: string;
  dueDate: string;
  submittedAt: string | null;
  complianceScore: number | null;
  band: VendorComplianceBand | null;
  vendor: { id: string; name: string; domain: string; inherentRisk: string };
  form: { id: string; key: string; name: string } | null;
  answeredCount: number;
  totalQuestions: number;
  completion: number;
}

interface PostureResponse {
  tiles: {
    total: number; completed: number; pending: number; expired: number;
    aligned: number; withGaps: number; withSignificantGaps: number;
  };
  items: PostureRow[];
  vendorOptions: Array<{ id: string; name: string }>;
  formOptions: Array<{ id: string; name: string }>;
}

/**
 * Band → tone. These are verdicts, so they use the reserved status ramp — never a
 * framework-identity colour. Aligned is good, minor gaps warn, significant gaps
 * (which a failed critical control forces) are critical.
 */
const BAND_TONE: Record<VendorComplianceBand, Tone> = {
  ALIGNED: 'good',
  WITH_GAPS: 'warning',
  WITH_SIGNIFICANT_GAPS: 'critical',
};

interface Assessment {
  id: string;
  vendorId: string;
  status: string;
  recipientEmail: string;
  emailSubject: string;
  language: string;
  dueDate: string;
  tokenExpiresAt: string;
  reminderOffsets: string;
  creditsCharged: number;
  complianceScore: number | null;
  submittedAt: string | null;
  createdAt: string;
  vendor: { id: string; name: string; domain: string; inherentRisk: string };
  form: { id: string; key: string; name: string } | null;
  answeredCount: number;
  totalQuestions: number;
  completion: number;
  remindersSent: number;
}

interface VendorForm {
  id: string; key: string; name: string; description: string | null;
  questionCount: number; creditCost: number; balanceAfter: number; affordable: boolean;
}

interface AnsweredQuestion {
  id: string; code: string; text: string; categoryPath: string | null;
  type: string; isRequired: boolean;
  options: Array<{ id: string; label: string; compliance?: string; score?: number }>;
  answer: { optionId: string | null; text: string | null; compliance?: string } | null;
}

/**
 * Status → tone.
 *
 * PENDING and IN_PROGRESS come from the shared map; the two statuses this screen
 * adds are spelled out. COMPLETED is good news, EXPIRED is a chase that failed —
 * which is a warning, not a catastrophe.
 */
const STATUS_TONE: Record<string, Tone> = {
  ...TONE_FOR_STATUS,
  COMPLETED: 'good',
  EXPIRED: 'serious',
};

/** The reminder schedules offered. Free-form day-offsets are an API-level detail. */
const REMINDER_PRESETS: Array<{ key: string; offsets: number[] }> = [
  { key: 'standard', offsets: [14, 7, 1] },
  { key: 'light', offsets: [7] },
  { key: 'insistent', offsets: [21, 14, 7, 3, 1] },
  { key: 'none', offsets: [] },
];

export function VendorAssessmentsPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [vendorFilter, setVendorFilter] = useState('');
  const [formFilter, setFormFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [bandFilter, setBandFilter] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The single posture view: tiles are computed server-side over the whole
  // organisation; the filters narrow the rows beneath them.
  const { data, isLoading, error: listError, refetch } = useQuery({
    queryKey: ['tprm', 'posture', { vendorFilter, formFilter, statusFilter, bandFilter }],
    queryFn: () =>
      api.get<PostureResponse>(
        `/tprm/posture${qs({
          vendorId: vendorFilter, formId: formFilter,
          status: statusFilter, band: bandFilter,
        })}`,
      ),
  });

  const tiles = data?.tiles;
  const rows = data?.items ?? [];
  const hasAny = (tiles?.total ?? 0) > 0;
  const anyFilter = !!(vendorFilter || formFilter || statusFilter || bandFilter);

  const formatDate = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso))
      : '—';

  const clearFilters = () => {
    setVendorFilter(''); setFormFilter(''); setStatusFilter(''); setBandFilter('');
  };

  /** The seven posture tiles, in the order the design lays them out. */
  const tileDefs: Array<{ label: string; value: number; tone?: Tone }> = tiles
    ? [
        { label: t('tprm.tileTotal', 'Total'), value: tiles.total },
        { label: t('tprm.tileCompleted', 'Completed'), value: tiles.completed,
          tone: tiles.completed > 0 ? 'good' : undefined },
        { label: t('tprm.tilePending', 'Pending / in progress'), value: tiles.pending,
          tone: tiles.pending > 0 ? 'warning' : undefined },
        { label: t('tprm.tileExpired', 'Expired'), value: tiles.expired,
          tone: tiles.expired > 0 ? 'serious' : undefined },
        { label: t('tprm.bandAligned', 'Aligned'), value: tiles.aligned,
          tone: tiles.aligned > 0 ? 'good' : undefined },
        { label: t('tprm.bandWithGaps', 'With gaps'), value: tiles.withGaps,
          tone: tiles.withGaps > 0 ? 'warning' : undefined },
        { label: t('tprm.bandSignificant', 'With significant gaps'), value: tiles.withSignificantGaps,
          tone: tiles.withSignificantGaps > 0 ? 'critical' : undefined },
      ]
    : [];

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="oc-h1">{t('tprm.title')}</h1>
          <p className="oc-muted mt-1">{t('tprm.subtitle')}</p>
        </div>
        {allows('vendor.assess') && (
          <Button size="sm" variant="primary" onClick={() => { setError(null); setSendOpen(true); }}>
            {t('tprm.send')}
          </Button>
        )}
      </header>

      {error && <ErrorNotice message={error} />}

      {hasAny && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
          {tileDefs.map((tile) => (
            <StatTile key={tile.label} label={tile.label} value={tile.value} tone={tile.tone} />
          ))}
        </div>
      )}

      <Card className="!p-3">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          <select className="oc-input sm:!w-auto" value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  aria-label={t('tprm.vendor')}>
            <option value="">{t('tprm.filterVendor', 'All vendors')}</option>
            {(data?.vendorOptions ?? []).map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <select className="oc-input sm:!w-auto" value={formFilter}
                  onChange={(e) => setFormFilter(e.target.value)}
                  aria-label={t('tprm.form')}>
            <option value="">{t('tprm.filterAssessment', 'All questionnaires')}</option>
            {(data?.formOptions ?? []).map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <select className="oc-input sm:!w-auto" value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label={t('tprm.statusFilter')}>
            <option value="">{t('tprm.filterStatus', 'All statuses')}</option>
            {VENDOR_ASSESSMENT_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`status.${s}`, { defaultValue: s })}</option>
            ))}
          </select>
          <select className="oc-input sm:!w-auto" value={bandFilter}
                  onChange={(e) => setBandFilter(e.target.value)}
                  aria-label={t('tprm.filterCompliance', 'Compliance')}>
            <option value="">{t('tprm.filterCompliance', 'All compliance')}</option>
            {VENDOR_COMPLIANCE_BANDS.map((b) => (
              <option key={b} value={b}>{t(`tprm.band.${b}`, bandLabelFallback(b))}</option>
            ))}
          </select>
          {anyFilter && (
            <button type="button" onClick={clearFilters}
                    className="text-sm text-brand hover:underline sm:self-center sm:ml-1">
              {t('common.clear', 'Clear')}
            </button>
          )}
        </div>
      </Card>

      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : listError ? (
        <ErrorNotice message={describeError(listError)} onRetry={() => void refetch()}
                     retryLabel={t('common.retry')} />
      ) : !hasAny ? (
        <Card>
          <EmptyState
            icon={<IconAssessment className="w-10 h-10" />}
            title={t('tprm.none')}
            description={t('tprm.noneHint')}
            action={allows('vendor.assess')
              ? <Button variant="primary" onClick={() => setSendOpen(true)}>{t('tprm.send')}</Button>
              : undefined}
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconAssessment className="w-10 h-10" />}
            title={t('tprm.noMatches', 'No assessments match these filters.')}
            action={<Button onClick={clearFilters}>{t('common.clear', 'Clear')}</Button>}
          />
        </Card>
      ) : (
        <>
          {/* Desktop: the posture table. */}
          <Card className="!p-0 hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-muted border-b border-line">
                  <th className="px-4 py-3 font-medium">{t('tprm.colVendor', 'Vendor')}</th>
                  <th className="px-4 py-3 font-medium">{t('tprm.colAssessment', 'Assessment')}</th>
                  <th className="px-4 py-3 font-medium">{t('tprm.colStatus', 'Status')}</th>
                  <th className="px-4 py-3 font-medium">{t('tprm.colCompletion', 'Completion')}</th>
                  <th className="px-4 py-3 font-medium">{t('tprm.colCompliance', 'Compliance')}</th>
                  <th className="px-4 py-3 font-medium">{t('tprm.colDue', 'Due')}</th>
                  <th className="px-4 py-3 font-medium">{t('tprm.colSubmitted', 'Submitted')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}
                      onClick={() => setDetailId(row.id)}
                      className="border-b border-line/60 last:border-0 cursor-pointer hover:bg-surface-sunken transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-ink">{row.vendor.name}</p>
                      <p className="text-xs text-ink-muted font-mono">{row.vendor.domain}</p>
                    </td>
                    <td className="px-4 py-3 text-ink-secondary">
                      {row.form?.name ?? t('tprm.formUnknown')}
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={STATUS_TONE[row.status] ?? 'neutral'}>
                        {t(`status.${row.status}`, { defaultValue: row.status })}
                      </Pill>
                    </td>
                    <td className="px-4 py-3">
                      {row.totalQuestions > 0 ? (
                        <span className="tabular-nums text-ink-secondary">{row.completion}%</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {row.band ? (
                        <Pill tone={BAND_TONE[row.band]}>
                          {t(`tprm.band.${row.band}`, bandLabelFallback(row.band))}
                          {row.complianceScore !== null ? ` · ${row.complianceScore}%` : ''}
                        </Pill>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-ink-secondary whitespace-nowrap">{formatDate(row.dueDate)}</td>
                    <td className="px-4 py-3 text-ink-secondary whitespace-nowrap">{formatDate(row.submittedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile: the same rows as cards. */}
          <ul className="grid gap-3 md:hidden">
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  onClick={() => setDetailId(row.id)}
                  className="oc-card p-4 w-full text-left hover:shadow-raised transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="font-medium text-ink truncate">{row.vendor.name}</p>
                      <p className="text-xs text-ink-muted truncate">
                        {row.form?.name ?? t('tprm.formUnknown')}
                      </p>
                    </div>
                    <Pill tone={STATUS_TONE[row.status] ?? 'neutral'} className="shrink-0">
                      {t(`status.${row.status}`, { defaultValue: row.status })}
                    </Pill>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
                    <span className="oc-pill-neutral">
                      {t('tprm.due', { date: formatDate(row.dueDate) })}
                    </span>
                    {row.band && (
                      <Pill tone={BAND_TONE[row.band]}>
                        {t(`tprm.band.${row.band}`, bandLabelFallback(row.band))}
                        {row.complianceScore !== null ? ` · ${row.complianceScore}%` : ''}
                      </Pill>
                    )}
                  </div>
                  {row.totalQuestions > 0 && (
                    <>
                      <ProgressBar
                        value={row.completion}
                        tone={row.status === 'COMPLETED' || row.status === 'FINALIZED' ? 'good' : 'brand'}
                        label={t('tprm.progressLabel')}
                      />
                      <p className="text-xs text-ink-muted mt-1.5">
                        {t('tprm.progress', { answered: row.answeredCount, total: row.totalQuestions })}
                      </p>
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <SendAssessmentModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSent={() => {
          setSendOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['tprm', 'posture'] });
          void queryClient.invalidateQueries({ queryKey: ['vendors'] });
          void queryClient.invalidateQueries({ queryKey: ['credits'] });
        }}
      />
      <AssessmentDetail assessmentId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

/** English fallback for a band label, used until the PM merges the i18n keys. */
function bandLabelFallback(band: VendorComplianceBand): string {
  switch (band) {
    case 'ALIGNED': return 'Aligned';
    case 'WITH_GAPS': return 'With gaps';
    case 'WITH_SIGNIFICANT_GAPS': return 'With significant gaps';
  }
}

// ---------------------------------------------------------------------------

/**
 * Sending a questionnaire.
 *
 * Credits are spent here, so the dialog states the exact cost and the resulting
 * balance, and requires an explicit acknowledgement — which the server also
 * insists on, so the warning cannot be skipped by calling the API directly.
 *
 * The recipient field carries the domain rule in its hint rather than only in an
 * error: being told up front that the address must be on the supplier's own
 * domain is better than being refused after filling in the whole form. The
 * server is still what enforces it.
 */
function SendAssessmentModal({ open, onClose, onSent }: {
  open: boolean; onClose: () => void; onSent: () => void;
}) {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();

  /** Default: four weeks out — long enough that a supplier can plan the work. */
  const defaultDue = () => {
    const date = new Date(Date.now() + 28 * 86_400_000);
    return date.toISOString().slice(0, 10);
  };

  const [vendorId, setVendorId] = useState('');
  const [formId, setFormId] = useState('');
  const [dueDate, setDueDate] = useState(defaultDue);
  const [language, setLanguage] = useState<Language>(() =>
    (LANGUAGES as readonly string[]).includes(i18n.language) ? (i18n.language as Language) : 'en',
  );
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [reminderPreset, setReminderPreset] = useState('standard');
  const [edited, setEdited] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: vendors } = useQuery({
    queryKey: ['vendors', 'assessable'],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; name: string; domain: string; contactEmail: string | null }> }>(
        '/vendors?isActive=true&pageSize=100',
      ),
    enabled: open,
  });

  const { data: forms } = useQuery({
    queryKey: ['tprm', 'forms'],
    queryFn: () => api.get<{ balance: number; forms: VendorForm[] }>('/tprm/forms'),
    enabled: open,
  });

  /**
   * The prefilled message comes from the server.
   *
   * Not built here: the reminder job sends mail with no client involved, so the
   * wording has to live in one place or a supplier gets two different voices
   * from the same organisation.
   */
  const dueIso = dueDate ? new Date(`${dueDate}T12:00:00.000Z`).toISOString() : '';
  const { data: draft } = useQuery({
    queryKey: ['tprm', 'draft', vendorId, formId, language, dueIso],
    queryFn: () =>
      api.get<{
        subject: string; body: string; recipientEmail: string | null; vendorDomain: string;
        creditCost: number; tokenExpiresAt: string; suggestedReminderOffsets: number[];
      }>(`/tprm/draft${qs({ vendorId, formId, language, dueDate: dueIso })}`),
    enabled: open && !!vendorId && !!formId && !!dueIso,
  });

  // Adopt the server's draft until the user starts typing, then leave their
  // words alone — silently overwriting an edited message when the language or
  // the due date changes would be infuriating. `edited` is the whole guard.
  useEffect(() => {
    if (!draft || edited) return;
    setSubject(draft.subject);
    setBody(draft.body);
    // Only ever FILLS the recipient. Never overwrites an address already typed,
    // and the vendor picker clears it, so a stale address cannot survive a
    // change of supplier.
    setRecipientEmail((current) => current || draft.recipientEmail || '');
  }, [draft, edited]);

  const selectedForm = forms?.forms.find((f) => f.id === formId);
  const selectedVendor = vendors?.items.find((v) => v.id === vendorId);
  const offsets = REMINDER_PRESETS.find((p) => p.key === reminderPreset)?.offsets ?? [];

  const send = useMutation({
    mutationFn: () =>
      api.post('/tprm/assessments', {
        vendorId,
        formId,
        recipientEmail: recipientEmail || undefined,
        dueDate: dueIso,
        language,
        subject,
        body,
        reminderOffsets: offsets,
        acknowledged: true,
      }),
    onSuccess: () => {
      setVendorId(''); setFormId(''); setSubject(''); setBody('');
      setRecipientEmail(''); setEdited(false); setAcknowledged(false); setError(null);
      onSent();
    },
    onError: (err) => setError(describeError(err)),
  });

  const ready = !!vendorId && !!formId && !!dueIso && !!subject.trim() && !!body.trim()
    && !!recipientEmail.trim() && acknowledged && selectedForm?.affordable;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tprm.send')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!ready || send.isPending}
                  onClick={() => send.mutate()}>
            {send.isPending ? t('common.saving') : t('tprm.confirmSend')}
          </Button>
        </>
      }
    >
      <div className="space-y-1">
        {error && <div className="mb-4"><ErrorNotice message={error} /></div>}

        <div className="rounded-xl bg-surface-sunken px-3 py-2.5 flex items-start gap-2 mb-4">
          <IconShield className="w-4 h-4 text-brand shrink-0 mt-0.5" />
          <p className="text-xs text-ink-secondary leading-relaxed">{t('tprm.sendNotice')}</p>
        </div>

        <Field label={t('tprm.vendor')} htmlFor="ta-vendor" required>
          <select id="ta-vendor" className="oc-input" value={vendorId}
                  onChange={(e) => {
                    setVendorId(e.target.value);
                    // The recipient belongs to the vendor, so it must not carry
                    // over — sending to the previous supplier's address is the
                    // exact mistake the domain rule exists to catch.
                    setRecipientEmail('');
                    setEdited(false);
                    setAcknowledged(false);
                  }}>
            <option value="">—</option>
            {(vendors?.items ?? []).map((v) => (
              <option key={v.id} value={v.id}>{v.name} · {v.domain}</option>
            ))}
          </select>
        </Field>

        <Field label={t('tprm.form')} htmlFor="ta-form" required>
          <select id="ta-form" className="oc-input" value={formId}
                  onChange={(e) => { setFormId(e.target.value); setEdited(false); setAcknowledged(false); }}>
            <option value="">—</option>
            {(forms?.forms ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} — {t('tprm.questionCount', { count: f.questionCount })}, {f.creditCost}
              </option>
            ))}
          </select>
          {selectedForm?.description && (
            <p className="mt-1.5 text-xs text-ink-secondary leading-relaxed">
              {selectedForm.description}
            </p>
          )}
        </Field>

        <Field
          label={t('tprm.recipient')}
          htmlFor="ta-email"
          hint={selectedVendor
            ? t('tprm.recipientHint', { domain: selectedVendor.domain })
            : t('tprm.recipientHintGeneric')}
          required
        >
          <input id="ta-email" type="email" className="oc-input" value={recipientEmail}
                 autoCapitalize="none" autoCorrect="off" maxLength={320}
                 onChange={(e) => setRecipientEmail(e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('tprm.dueDate')} htmlFor="ta-due" required>
            <input id="ta-due" type="date" className="oc-input" value={dueDate}
                   min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
                   onChange={(e) => { setDueDate(e.target.value); setAcknowledged(false); }} />
          </Field>
          <Field label={t('common.language')} htmlFor="ta-lang">
            <select id="ta-lang" className="oc-input" value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value as Language);
                      // Only re-draft when they have not written their own
                      // message; `edited` is what protects their words.
                      if (!edited) { setSubject(''); setBody(''); }
                    }}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>{LANGUAGE_NAMES[l]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={t('tprm.reminders')} htmlFor="ta-reminders"
               hint={offsets.length === 0
                 ? t('tprm.remindersNoneHint')
                 : t('tprm.remindersHint', { days: offsets.join(', ') })}>
          <select id="ta-reminders" className="oc-input" value={reminderPreset}
                  onChange={(e) => setReminderPreset(e.target.value)}>
            {REMINDER_PRESETS.map((preset) => (
              <option key={preset.key} value={preset.key}>
                {t(`tprm.reminderPreset.${preset.key}`)}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('tprm.subject')} htmlFor="ta-subject" required>
          <input id="ta-subject" className="oc-input" value={subject} maxLength={200}
                 onChange={(e) => { setSubject(e.target.value); setEdited(true); }} />
        </Field>

        <Field label={t('tprm.body')} htmlFor="ta-body" hint={t('tprm.bodyHint')} required>
          <textarea id="ta-body" className="oc-input min-h-[9rem] py-2 resize-y" value={body}
                    maxLength={5000}
                    onChange={(e) => { setBody(e.target.value); setEdited(true); }} />
        </Field>

        {/* --- Cost, then the acknowledgement -------------------------------- */}
        {selectedForm && (
          <>
            <dl className="rounded-xl bg-surface-sunken p-4 space-y-2 text-sm mt-1">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-secondary">{t('tprm.cost')}</dt>
                <dd className="tabular-nums font-medium text-ink">{selectedForm.creditCost}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-secondary">{t('tprm.balance')}</dt>
                <dd className="tabular-nums font-medium text-ink">{forms?.balance ?? 0}</dd>
              </div>
              <div className="flex justify-between gap-3 pt-2 border-t border-line">
                <dt className="text-ink-secondary">{t('tprm.balanceAfter')}</dt>
                <dd className={cn(
                  'tabular-nums font-medium',
                  selectedForm.affordable ? 'text-ink' : 'text-critical',
                )}>
                  {selectedForm.balanceAfter}
                </dd>
              </div>
            </dl>

            {!selectedForm.affordable && (
              <p className="mt-2 text-xs text-critical">{t('tprm.notAffordable')}</p>
            )}

            {draft && (
              <p className="mt-2 text-xs text-ink-muted leading-relaxed">
                {t('tprm.linkExpiryNote', {
                  date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' })
                    .format(new Date(draft.tokenExpiresAt)),
                })}
              </p>
            )}

            <label className="flex items-start gap-3 cursor-pointer mt-3">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))]"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span className="text-sm text-ink">
                {t('tprm.acknowledge', {
                  count: selectedForm.creditCost,
                  email: recipientEmail || '—',
                })}
              </span>
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * One assessment, and what the supplier said.
 *
 * Answers are shown with the verdict each one carries, because the score on its
 * own is not actionable — "72%" does not tell you which control is missing. The
 * non-compliant answers are what somebody follows up on, so they are legible at a
 * glance rather than buried in a list of 147.
 */
function AssessmentDetail({ assessmentId, onClose }: {
  assessmentId: string | null; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: assessment, isLoading } = useQuery({
    queryKey: ['vendor-assessment', assessmentId],
    queryFn: () =>
      api.get<Omit<Assessment, 'vendor' | 'form'> & {
        emailBody: string;
        band: VendorComplianceBand | null;
        vendor: { id: string; name: string; domain: string; inherentRisk: string };
        form: { id: string; key: string; name: string; description: string | null } | null;
        sections: Array<{
          code: string; title: string; answered: number; total: number;
          questions: AnsweredQuestion[];
        }>;
      }>(`/tprm/assessments/${assessmentId}`),
    enabled: !!assessmentId,
  });

  const revoke = useMutation({
    mutationFn: () => api.delete(`/tprm/assessments/${assessmentId}`),
    onSuccess: () => {
      setConfirmRevoke(false);
      void queryClient.invalidateQueries({ queryKey: ['vendor-assessment', assessmentId] });
      void queryClient.invalidateQueries({ queryKey: ['tprm', 'posture'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  const formatDate = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso))
      : '—';

  if (!assessmentId) return null;

  const isOpen = assessment?.status === 'PENDING' || assessment?.status === 'IN_PROGRESS';

  return (
    <Modal
      open={!!assessmentId}
      onClose={onClose}
      title={assessment?.vendor.name ?? t('common.loading')}
    >
      {isLoading || !assessment ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : (
        <div className="space-y-5">
          {error && <ErrorNotice message={error} />}

          <div>
            <p className="text-sm text-ink-secondary">
              {assessment.form?.name ?? t('tprm.formUnknown')}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Pill tone={STATUS_TONE[assessment.status] ?? 'neutral'}>
                {t(`status.${assessment.status}`, { defaultValue: assessment.status })}
              </Pill>
              <span className="oc-pill-neutral">
                {t('tprm.due', { date: formatDate(assessment.dueDate) })}
              </span>
              {assessment.band && (
                <Pill tone={BAND_TONE[assessment.band]}>
                  {t(`tprm.band.${assessment.band}`, bandLabelFallback(assessment.band))}
                </Pill>
              )}
              {assessment.complianceScore !== null && (
                <Pill tone={assessment.complianceScore >= 70 ? 'good' : 'serious'}>
                  {t('tprm.score', { count: assessment.complianceScore })}
                </Pill>
              )}
            </div>
          </div>

          {/* --- Delivery -------------------------------------------------- */}
          <section>
            <h3 className="oc-h2 text-base mb-3">{t('tprm.deliveryTitle')}</h3>
            <dl className="rounded-xl bg-surface-sunken p-3 space-y-1.5 text-xs">
              {([
                ['tprm.recipient', assessment.recipientEmail],
                ['tprm.subject', assessment.emailSubject],
                ['tprm.sentOn', formatDate(assessment.createdAt)],
                ['tprm.linkExpires', formatDate(assessment.tokenExpiresAt)],
                ['tprm.remindersSent', String(assessment.remindersSent)],
                ['tprm.costPaid', String(assessment.creditsCharged)],
                ['tprm.submittedOn', formatDate(assessment.submittedAt)],
              ] as const).map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="text-ink-secondary shrink-0">{t(label)}</dt>
                  <dd className="text-ink text-right break-words min-w-0">{value}</dd>
                </div>
              ))}
            </dl>
            {/* The link itself is deliberately absent: only its SHA-256 is
                stored, so there is nothing to show — and nothing to steal. */}
            <p className="mt-2 text-xs text-ink-muted leading-relaxed">{t('tprm.linkNotShown')}</p>
          </section>

          {/* --- Progress -------------------------------------------------- */}
          <section>
            <ProgressBar
              value={assessment.completion}
              tone={assessment.status === 'COMPLETED' ? 'good' : 'brand'}
              label={t('tprm.progressLabel')}
            />
            <p className="text-xs text-ink-muted mt-1.5">
              {t('tprm.progress', {
                answered: assessment.answeredCount,
                total: assessment.totalQuestions,
              })}
            </p>
          </section>

          {/* --- Answers --------------------------------------------------- */}
          <section>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="oc-h2 text-base">{t('tprm.answersTitle')}</h3>
              <label className="flex items-center gap-2 text-xs text-ink-secondary cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-[rgb(var(--brand))]"
                       checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
                {t('tprm.onlyProblems')}
              </label>
            </div>

            {assessment.answeredCount === 0 ? (
              <p className="text-sm text-ink-muted">{t('tprm.noAnswersYet')}</p>
            ) : (
              <div className="space-y-4">
                {assessment.sections.map((section) => {
                  const questions = section.questions.filter((question) => {
                    if (!question.answer) return false;
                    if (!onlyProblems) return true;
                    return question.answer.compliance === 'NON_COMPLIANT'
                      || question.answer.compliance === 'NEEDS_IMPROVEMENT';
                  });
                  if (questions.length === 0) return null;

                  return (
                    <div key={section.code}>
                      <p className="text-xs font-medium uppercase tracking-wide text-ink-muted mb-2">
                        {section.title} · {section.answered}/{section.total}
                      </p>
                      <ul className="space-y-1.5">
                        {questions.map((question) => {
                          const chosen = question.options.find(
                            (o) => o.id === question.answer?.optionId,
                          );
                          return (
                            <li key={question.id} className="rounded-xl bg-surface-sunken p-3">
                              <p className="text-xs text-ink-muted font-mono mb-1">{question.code}</p>
                              <p className="text-sm text-ink leading-relaxed">{question.text}</p>
                              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                {chosen ? (
                                  <Pill tone={TONE_FOR_STATUS[chosen.compliance ?? ''] ?? 'neutral'}>
                                    {chosen.label}
                                  </Pill>
                                ) : (
                                  /* Free-text questions carry no verdict — the
                                     supplier's words are the whole answer. */
                                  <span className="oc-pill-neutral">{t('tprm.freeText')}</span>
                                )}
                              </div>
                              {question.answer?.text && (
                                <p className="text-xs text-ink-secondary mt-2 leading-relaxed
                                              whitespace-pre-wrap border-l-2 border-brand/30 pl-3">
                                  {question.answer.text}
                                </p>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* --- Revoke ---------------------------------------------------- */}
          {isOpen && allows('vendor.assess') && (
            <section className="pt-1">
              {confirmRevoke ? (
                <div className="rounded-xl border border-critical/30 bg-critical/[0.06] p-3">
                  <p className="text-sm text-ink leading-relaxed">{t('tprm.revokeWarning')}</p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={() => setConfirmRevoke(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button size="sm" variant="danger" disabled={revoke.isPending}
                            onClick={() => revoke.mutate()}>
                      {revoke.isPending ? t('common.saving') : t('tprm.revokeConfirm')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" onClick={() => { setError(null); setConfirmRevoke(true); }}>
                  {t('tprm.revoke')}
                </Button>
              )}
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
